/**
 * Habilitation ANAH — ESPACE DOCUMENTAIRE DE LA STRUCTURE (#511, V1).
 *
 * Ouvrir OmniHub, voir ce qui est prêt, télécharger, envoyer. Plus de fouille
 * dans Drive, Zoho ou sur disque pour constituer un dossier administratif.
 *
 * DEUX NIVEAUX
 *  · Bibliothèque — les pièces de la structure, déposées UNE fois.
 *  · Packs        — une demande précise, qui RÉSOUT ses pièces sur la
 *                   bibliothèque. Aucune copie : un Kbis remplacé est à jour
 *                   dans les quatre packs à l'instant même.
 *
 * ⚠️ La liste des pièces jointes est GÉNÉRÉE par le serveur depuis l'archive
 * réelle. Cet écran ne la compose pas et n'a aucun moyen de la composer : il
 * n'affiche que ce que le pack contient (présentes) et ce qui lui manque.
 *
 * Le statut affiché est celui de la FRAÎCHEUR d'une pièce. Le jugement de
 * conformité viendra du Hub Conformité — il n'est pas rendu ici.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DestinatairesPanel from "./habilitation/DestinatairesPanel";
import AnnuaireEcran from "./habilitation/AnnuaireEcran";
import {
  AlertTriangle, Archive, CheckCircle2, ClipboardList, Eye, FileDown, FilePlus2,
  Loader2, RefreshCw, Send, Sparkles, Trash2,
} from "lucide-react";
import ChampDate from "../../components/ui/champ-date";

const API = process.env.REACT_APP_BACKEND_URL || "";

const TONS_STATUT = {
  valide: "green",
  bientot_perime: "amber",
  perime: "red",
  sans_echeance: "blue",
  // ⚠️ #671 — « date inconnue » n'est ni verte ni rouge : on ne SAIT PAS.
  date_inconnue: "amber",
};

// 🔴 #860 — LE STATUT D'UN PACK EST DÉSORMAIS PROJETÉ de ses démarches.
// ⚠️ C'est un RÉSUMÉ pour une liste, ⛔ jamais de quoi décider : le 78 peut être
// habilité pendant que le 94 est refusé, et aucun mot unique ne dit ça.
const LIBELLES_STATUT_PACK = {
  en_preparation: "En préparation",
  consigne_manuellement: "Pli consigné manuellement",
  partiellement_engage: "Partiellement engagé",
  en_cours: "En cours",
  decide: "Décidé",
  habilite: "Habilité",
};
const TONS_STATUT_PACK = {
  en_preparation: "amber",
  consigne_manuellement: "cyan",
  partiellement_engage: "amber",
  en_cours: "blue",
  decide: "purple",
  habilite: "green",
};

const LIBELLES_STATUT = {
  valide: "Valide",
  bientot_perime: "Bientôt périmé",
  perime: "Périmé",
  sans_echeance: "Sans échéance",
  date_inconnue: "Date inconnue",
};

const dateFR = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("fr-FR");
};

const poids = (octets) => {
  if (octets === null || octets === undefined) return "—";
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
};

// ─────────────────────────────────────────────────────────────────────────────
//  Briques charte — tokens `--oh-*` uniquement, deux modes par construction
// ─────────────────────────────────────────────────────────────────────────────
const Carte = ({ titre, sousTitre, action, children }) => (
  <section
    style={{
      background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
      borderRadius: 12, padding: 16, marginBottom: 16,
    }}
  >
    {(titre || action) && (
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 500, color: "var(--oh-text)", margin: 0 }}>{titre}</h2>
          {sousTitre && (
            <p style={{ fontSize: 12.5, color: "var(--oh-text-secondary)", margin: "4px 0 0", lineHeight: 1.6 }}>
              {sousTitre}
            </p>
          )}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

const Badge = ({ ton, children, titre }) => (
  <span
    title={titre}
    style={{
      background: `var(--oh-stat-${ton}-bg)`, color: `var(--oh-stat-${ton}-text)`,
      borderRadius: 8, padding: "2px 8px", fontSize: 12, fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const BoutonPrimaire = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)",
      border: "none", borderRadius: 8, height: 36, padding: "0 16px",
      fontSize: 13, fontWeight: 500, cursor: props.disabled ? "not-allowed" : "pointer",
      opacity: props.disabled ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 8,
      ...(style || {}),
    }}
  >
    {children}
  </button>
);

const BoutonSecondaire = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      background: "transparent", color: "var(--oh-accent-text)",
      border: "1px solid var(--oh-accent)", borderRadius: 8, height: 36, padding: "0 14px",
      fontSize: 13, fontWeight: 500, cursor: props.disabled ? "not-allowed" : "pointer",
      opacity: props.disabled ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 8,
      ...(style || {}),
    }}
  >
    {children}
  </button>
);

const BoutonDiscret = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      background: "transparent", color: "var(--oh-text-secondary)",
      border: "1px solid var(--oh-border)", borderRadius: 8, height: 32, padding: "0 12px",
      fontSize: 12.5, cursor: props.disabled ? "not-allowed" : "pointer",
      opacity: props.disabled ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 6,
      ...(style || {}),
    }}
  >
    {children}
  </button>
);

const champStyle = {
  height: 36, borderRadius: 8, padding: "0 10px", fontSize: 13,
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", width: "100%",
};

const labelStyle = { fontSize: 12.5, color: "var(--oh-text-secondary)" };

// ─────────────────────────────────────────────────────────────────────────────
//  Téléchargement — le serveur reste la seule source du fichier
// ─────────────────────────────────────────────────────────────────────────────
async function telecharger(url, nomParDefaut) {
  const r = await fetch(`${API}${url}`, { credentials: "include" });
  if (!r.ok) throw new Error(`Téléchargement impossible (${r.status})`);
  const disposition = r.headers.get("Content-Disposition") || "";
  const trouve = /filename="?([^"]+)"?/.exec(disposition);
  const blob = await r.blob();
  const objet = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objet;
  a.download = trouve ? trouve[1] : nomParDefaut;
  a.click();
  URL.revokeObjectURL(objet);
}

// ⚠️ VOIR ≠ TÉLÉCHARGER. « Voir » ouvre la pièce dans un onglet ; c'est ce qu'on
// fait avant de valider un envoi — on regarde, on ne collectionne pas des
// fichiers. Le mécanisme est celui déjà éprouvé ailleurs dans OmniHub
// (`helpers.js:openAuthedPdf`) : ⚠️ l'onglet est ouvert SYNCHRONEMENT, avant le
// `await`. ⛔ Un `window.open` appelé après le fetch est tué par le bloqueur de
// pop-ups — le bouton semblerait alors ne rien faire.
async function voir(url, onErreur) {
  const onglet = window.open("", "_blank");
  try {
    const r = await fetch(`${API}${url}`, { credentials: "include" });
    if (!r.ok) throw new Error(`Ouverture impossible (${r.status})`);
    const objet = URL.createObjectURL(await r.blob());
    if (onglet) onglet.location = objet;
    else window.location.assign(objet);
  } catch (e) {
    if (onglet) onglet.close();
    onErreur(e.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Génération d'une pièce — ⛔ aucun code de pièce en dur ici
// ─────────────────────────────────────────────────────────────────────────────
// 🔴 L'écran ne sait PAS ce qu'est un « relevé de prestations ». Il lit
// `generable` sur l'exigence, renvoyé par le backend depuis son registre. Le
// jour où une deuxième pièce devient productible, ce bouton apparaît tout seul.
// ⛔ Un `if (type_piece === "hab_releve_prestations")` ici divergerait du backend
// dès la deuxième pièce, et personne ne le verrait avant la production.
async function genererPiece(typePiece, action = "generer") {
  const r = await fetch(
    `${API}/api/metier/habilitation/pieces/${typePiece}/${action}`,
    {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      // ⚠️ Corps vide = le backend applique ses valeurs par défaut (date d'arrêt
      // = aujourd'hui). ⛔ L'écran n'invente aucun paramètre métier.
      body: JSON.stringify({}),
    }
  );
  if (!r.ok) {
    const detail = await r.json().catch(() => ({}));
    throw new Error(detail.detail || `Génération impossible (${r.status})`);
  }
  return r.json();
}

// ─────────────────────────────────────────────────────────────────────────────
//  Dépôt d'une pièce
// ─────────────────────────────────────────────────────────────────────────────
function FormulaireDepot({ referentiel, packs, onDepose, onErreur }) {
  const [typePiece, setTypePiece] = useState("");
  const [fichier, setFichier] = useState(null);
  const [dateDocument, setDateDocument] = useState("");
  const [dateExpiration, setDateExpiration] = useState("");
  const [packCode, setPackCode] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const definition = useMemo(
    () => (referentiel?.pieces || []).find((p) => p.code === typePiece),
    [referentiel, typePiece]
  );

  const soumettre = async (e) => {
    e.preventDefault();
    if (!typePiece || !fichier) return;
    const formulaire = e.target;
    setEnvoi(true);
    try {
      const corps = new FormData();
      corps.append("fichier", fichier);
      corps.append("type_piece", typePiece);
      if (dateDocument) corps.append("date_document", dateDocument);
      if (dateExpiration) corps.append("date_expiration", dateExpiration);
      if (packCode) corps.append("pack_code", packCode);
      const r = await fetch(`${API}/api/metier/habilitation/bibliotheque`, {
        method: "POST", credentials: "include", body: corps,
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `Dépôt impossible (${r.status})`);
      }
      setFichier(null); setDateDocument(""); setDateExpiration(""); setPackCode("");
      formulaire.reset();
      await onDepose();
    } catch (err) {
      onErreur(err.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form onSubmit={soumettre} data-testid="habilitation-depot">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <label style={labelStyle}>
          Type de pièce
          <select
            style={{ ...champStyle, marginTop: 4 }} value={typePiece}
            onChange={(e) => { setTypePiece(e.target.value); setPackCode(""); }}
            aria-label="Type de pièce" required
          >
            <option value="">Choisir…</option>
            {(referentiel?.pieces || []).map((p) => (
              <option key={p.code} value={p.code}>{p.libelle}</option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          Fichier
          <input
            type="file" style={{ ...champStyle, marginTop: 4, paddingTop: 6 }}
            onChange={(e) => setFichier(e.target.files?.[0] || null)}
            aria-label="Fichier de la pièce" required
          />
        </label>

        <label style={labelStyle}>
          Date du document
          <ChampDate  style={{ ...champStyle, marginTop: 4 }} value={dateDocument}
                 onChange={(e) => setDateDocument(e.target.value)}
                 aria-label="Date du document" />
        </label>

        <label style={labelStyle}>
          Date d&apos;expiration
          <ChampDate
             style={{ ...champStyle, marginTop: 4 }} value={dateExpiration}
            onChange={(e) => setDateExpiration(e.target.value)}
            disabled={Boolean(definition) && !definition.expiration}
            aria-label="Date d'expiration"
          />
        </label>

        {definition?.specifique_pack && (
          <label style={labelStyle}>
            Demande concernée
            <select style={{ ...champStyle, marginTop: 4 }} value={packCode}
                    onChange={(e) => setPackCode(e.target.value)}
                    aria-label="Demande concernée" required>
              <option value="">Choisir…</option>
              {packs.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
            </select>
          </label>
        )}
      </div>

      {definition?.aide && (
        <p style={{ fontSize: 12, color: "var(--oh-text-muted)", margin: "8px 0 0", lineHeight: 1.6 }}>
          {definition.aide}
        </p>
      )}

      <div style={{ marginTop: 12 }}>
        <BoutonSecondaire type="submit" disabled={envoi || !typePiece || !fichier}
                          data-testid="habilitation-deposer">
          {envoi ? <Loader2 size={15} className="animate-spin" /> : <FilePlus2 size={15} />}
          Déposer la pièce
        </BoutonSecondaire>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Bibliothèque
// ─────────────────────────────────────────────────────────────────────────────
function Bibliotheque({ pieces, onRetirer, onErreur }) {
  const cellule = { padding: "8px 10px", fontSize: 13, color: "var(--oh-text)", textAlign: "left" };
  const entete = { ...cellule, fontSize: 12, fontWeight: 500, color: "var(--oh-text-secondary)" };

  if (!pieces.length) {
    return (
      <p style={{ fontSize: 13, color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
        Aucune pièce déposée. Les packs sont donc tous incomplets — c&apos;est dit,
        pas masqué.
      </p>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
        <thead>
          <tr style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
            <th style={entete}>Pièce</th>
            <th style={entete}>Type</th>
            <th style={entete}>Date</th>
            <th style={entete}>Échéance</th>
            <th style={entete}>Statut</th>
            <th style={entete}>Version</th>
            <th style={entete}>Source</th>
            <th style={entete}>Poids</th>
            <th style={entete}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pieces.map((p) => (
            <tr key={p.id} style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
              <td style={cellule}>
                {p.fichier_nom || p.nom}
                {p.pack_code && (
                  <span style={{ fontSize: 11, color: "var(--oh-text-muted)", display: "block" }}>
                    propre à la demande « {p.pack_code} »
                  </span>
                )}
              </td>
              <td style={cellule}>{p.type_libelle}</td>
              {/* 🔴 #671 — ⛔ PLUS DE REPLI SUR `created_at`. La date de DÉPÔT tenait lieu
                  de date d'ÉMISSION : un Kbis émis il y a 4 mois s'affichait comme
                  s'il datait du jour. Vide = on ne sait pas, et ça se voit. */}
              <td style={cellule}>{p.date_document ? dateFR(p.date_document) : "—"}</td>
              <td style={cellule}>
                {p.statut?.date_echeance ? dateFR(p.statut.date_echeance) : "—"}
              </td>
              <td style={cellule}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Badge ton={TONS_STATUT[p.statut?.code] || "blue"}>
                    {p.statut?.libelle || LIBELLES_STATUT[p.statut?.code] || "—"}
                  </Badge>
                  {/* ⚠️ #671 — VISIBLE, ⛔ plus une infobulle au survol : une
                      estimation qu'il faut survoler pour connaître se lit comme
                      un fait. */}
                  {p.statut?.indicatif && (
                    <span style={{ fontSize: 11.5, color: "var(--oh-text-secondary)" }}>
                      estimation
                    </span>
                  )}
                  {p.statut?.action_attendue && (
                    <span style={{ fontSize: 11.5, color: "var(--oh-stat-amber-text)" }}>
                      {p.statut.action_attendue}
                    </span>
                  )}
                </span>
              </td>
              <td style={cellule}>v{p.version}</td>
              <td style={cellule}>{p.source}</td>
              <td style={cellule}>{poids(p.fichier_taille)}</td>
              <td style={cellule}>
                <div style={{ display: "flex", gap: 6 }}>
                  <BoutonDiscret
                    onClick={() => telecharger(p.telechargement, p.fichier_nom).catch((e) => onErreur(e.message))}
                    aria-label={`Télécharger ${p.type_libelle}`}
                  >
                    <FileDown size={14} /> Télécharger
                  </BoutonDiscret>
                  <BoutonDiscret onClick={() => onRetirer(p)}
                                 aria-label={`Retirer ${p.type_libelle}`}>
                    <Trash2 size={14} />
                  </BoutonDiscret>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Détail d'un pack
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
//  BROUILLON D'ENVOI (#781) — PRÉPARER · RELIRE · VALIDER
// ─────────────────────────────────────────────────────────────────────────────
// 🔴 UN ENVOI ANAH PART UNE FOIS. Il n'existe pas d'« annuler l'envoi ». La
// seule protection possible est AVANT : que JJ VOIE le destinataire, le
// périmètre, l'objet, le corps et la liste exacte des pièces avant de valider.
//
// ⚠️ LE PÉRIMÈTRE SE COCHE, il ne se devine pas. `extension_78_94` ⛔ ne dit pas
// si le pli couvre le 78, le 94, ou les deux — c'est le défaut que le #775
// ferme. ⛔ Aucune case n'est pré-cochée : sans coche, le backend REFUSE.

// ⚠️ Les options PROPOSÉES, ⛔ pas déduites : JJ coche ce que le pli couvre
// réellement. Un pack d'extension multi-départements est proposé département
// par département, parce que c'est ainsi que l'instruction se fait ; les autres
// restent d'un bloc. ⚠️ Ce n'est qu'une PROPOSITION d'écran — la déclaration,
// c'est la coche.
//
// 🔴 #782 — SEULES LES DEMANDES DU MÊME DESTINATAIRE SONT PROPOSABLES.
// L'écran offrait de cocher le 60 et le 06 depuis le pack IDF. Or DRIHL
// Île-de-France ≠ DDT de l'Oise ≠ DDTM des Alpes-Maritimes : ⛔ un pli adressé
// à la DRIHL ne peut PAS couvrir l'Oise. Le courrier DRIHL le dit lui-même —
// les extensions qu'il accepte au renouvellement sont FRANCILIENNES.
// ⚠️ Le filtre porte sur le DESTINATAIRE, ⛔ pas sur une liste de départements
// codée en dur : le jour où un pack change d'administration, l'écran suit.
// 🔴 #826 — `perimetresProposes` A ÉTÉ RETIRÉE. Elle reconstruisait la liste
// des demandes CÔTÉ ÉCRAN, en parallèle du backend. Deux constructions
// parallèles divergent toujours — et c'est cette divergence qui a produit le
// défaut du #825. La liste vient désormais de `GET .../perimetres`, seule
// source, dérivée du référentiel.

function BrouillonEnvoi({ pack, packs, onFerme, onErreur, onEnvoye }) {
  const [brouillon, setBrouillon] = useState(null);
  const [repris, setRepris] = useState(false);
  const [choix, setChoix] = useState(null);
  const [perimetre, setPerimetre] = useState([]);   // ⛔ vide par défaut
  // 🔴 #796 — LA FORME DU PLI. Le nominal est « pièces séparées » : c'est ainsi
  // que les envois à la DRIHL ont abouti, et certaines passerelles filtrent les
  // `.zip`. L'archive reste possible, mais elle se CHOISIT.
  const [mode, setMode] = useState("pieces_separees");
  // ⛔ Aucun défaut : un essai et un pli administratif ne se devinent pas.
  const [nature, setNature] = useState("");
  const [confirmer, setConfirmer] = useState(false);
  const [occupe, setOccupe] = useState(null);
  const [alerte, setAlerte] = useState(null);

  // 🔴 #826 — LES DEMANDES VIENNENT DU BACKEND, ⛔ plus d'une reconstruction
  // locale. Deux constructions parallèles finissent toujours par diverger — et
  // c'est cette divergence qui a produit le défaut du #825.
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let vivant = true;
    fetch(`${API}/api/metier/habilitation/perimetres?destinataire=`
          + encodeURIComponent(pack.destinataire || ""),
          { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { demandes: [] }))
      .then((d) => { if (vivant) setOptions(d.demandes || []); })
      .catch(() => { if (vivant) setOptions([]); });
    return () => { vivant = false; };
  }, [pack.destinataire]);

  // 🔴 REPRENDRE PLUTOT QUE RECREER. Sans cette recherche, chaque ouverture du
  // panneau creait un brouillon NEUF : en production le pli 06 en a produit
  // trois, et JJ relisait un brouillon vide en croyant relire celui ou il avait
  // saisi 463 caracteres. « Enregistrer » enregistrait alors du vide, sans rien
  // signaler — un bouton qui « ne fait rien ».
  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/metier/habilitation/brouillons?pack_code=`
          + encodeURIComponent(pack.code), { credentials: "include" });
        if (!r.ok) return;
        const rep = await r.json();
        if (!vivant) return;
        // ⚠️ UN SEUL brouillon ⇒ on le reprend. PLUSIEURS ⇒ on DEMANDE.
        // ⛔ Reprendre « le plus récent » aurait rendu le brouillon VIDE de
        // 16 h alors que celui de 9 h portait les 463 caractères saisis.
        if (rep.en_cours) { setBrouillon(rep.en_cours); setRepris(true); }
        else if (rep.choix_requis) { setChoix(rep.resumes || []); }
      } catch { /* pas de reprise possible : on preparera comme avant */ }
    })();
    return () => { vivant = false; };
  }, [pack.code]);

  const preparer = async () => {
    setOccupe("preparer"); setAlerte(null);
    try {
      const r = await fetch(
        `${API}/api/metier/habilitation/packs/${pack.code}/brouillons`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          // ⛔ PLUS D'ADRESSE ICI : elle se choisit APRÈS, dans le panneau.
          body: JSON.stringify({ destinataire_email: null,
                                 mode_envoi: mode,
                                 // 🔴 #826 — LE PÉRIMÈTRE ENTRE ICI. Le sujet et
                                 // le corps seront écrits À PARTIR de lui.
                                 perimetre_metier: perimetre }) });
      if (!r.ok) throw new Error((await r.json()).detail || `Préparation impossible (${r.status})`);
      setBrouillon(await r.json());
    } catch (e) { onErreur(e.message); } finally { setOccupe(null); }
  };

  const envoyer = async () => {
    setOccupe("envoyer"); setAlerte(null);
    try {
      const r = await fetch(
        `${API}/api/metier/habilitation/brouillons/${brouillon.id}/envoyer`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // ⚠️ La validation est EXPLICITE : un POST accidentel sur cette URL
            // ⛔ ne doit pas suffire à faire partir un pli.
            valide: true,
            // 🔴 #826 — ⛔ PLUS DE `perimetre_metier` ICI. Il est figé sur le
            // brouillon, et la route le REFUSE s'il est envoyé : un client resté
            // en arrière croirait le choisir alors que le pli annonce autre
            // chose. C'est exactement le défaut qu'on ferme.
            nature,
            confirmer_renvoi: confirmer,
            // ⛔ PLUS DE `destinataires_cc` ICI. Les copies vivent sur le
            // BROUILLON, avec le corps qu'elles ont contribué à composer. Le
            // paramètre d'envoi s'y AJOUTAIT : l'envoyer encore doublerait
            // chaque adresse déjà saisie dans le panneau.
          }) });
      const corps = await r.json();
      if (!r.ok) {
        // ⚠️ L'ALERTE « déjà transmis » n'est PAS une erreur : c'est une
        // question posée à JJ. ⛔ Le double-clic silencieux est ce qu'on
        // empêche, pas le renvoi volontaire.
        if (String(corps.detail || "").includes("ALERTE")) {
          setAlerte(corps.detail); return;
        }
        throw new Error(corps.detail || `Envoi refusé (${r.status})`);
      }
      onEnvoye(corps);
    } catch (e) { onErreur(e.message); } finally { setOccupe(null); }
  };

  const abandonner = async () => {
    if (brouillon && window.confirm("Abandonner ce brouillon ? Rien ne sera envoyé.")) {
      await fetch(`${API}/api/metier/habilitation/brouillons/${brouillon.id}`,
                  { method: "DELETE", credentials: "include" });
    }
    onFerme();
  };

  const champ = { ...champStyle, maxWidth: 460 };

  return (
    <div style={{ marginTop: 12, padding: 16, borderRadius: 12,
                  border: "1px solid var(--oh-accent)",
                  background: "var(--oh-bg-surface)" }}
         data-testid={`habilitation-brouillon-${pack.code}`}>
      <h3 style={{ fontSize: 14, fontWeight: 500, color: "var(--oh-text)", margin: "0 0 4px" }}>
        Préparer l&apos;envoi — {pack.libelle}
      </h3>
      <p style={{ fontSize: 12.5, color: "var(--oh-text-secondary)", margin: "0 0 12px", lineHeight: 1.6 }}>
        Un envoi ANAH part une fois. Arrêtez d&apos;abord le périmètre : le sujet
        et le corps en découlent. Vous choisirez ensuite les destinataires, et
        vous relirez le message avant de valider — il n&apos;y a pas
        d&apos;annulation.
      </p>

      {/* 🔴 PLUSIEURS BROUILLONS EN COURS — l'opérateur choisit. La machine
          ⛔ ne devine pas lequel est « le bon » : en production, le plus
          récent était vide et celui qui portait le texte avait sept heures. */}
      {!brouillon && choix && choix.length > 0 && (
        <div style={{ marginBottom: 14, padding: 12, borderRadius: 8,
                      background: "var(--oh-stat-amber-bg)",
                      color: "var(--oh-stat-amber-text)" }}
             data-testid="brouillon-choix">
          <strong style={{ fontWeight: 500, fontSize: 13 }}>
            {choix.length} brouillons sont en cours pour ce pack
          </strong>
          <p style={{ fontSize: 12.5, lineHeight: 1.6, margin: "4px 0 10px" }}>
            Reprenez le vôtre — celui qui porte votre texte. Les autres
            resteront intacts ; vous pourrez les abandonner ensuite.
          </p>
          <div style={{ display: "grid", gap: 6 }}>
            {choix.map((c) => (
              <button key={c.id} type="button"
                      data-testid={`brouillon-choix-${c.id}`}
                      onClick={async () => {
                        const r = await fetch(
                          `${API}/api/metier/habilitation/brouillons/${c.id}`,
                          { credentials: "include" });
                        if (r.ok) { setBrouillon(await r.json());
                                    setRepris(true); setChoix(null); }
                      }}
                      style={{ textAlign: "left", padding: "8px 10px",
                               borderRadius: 8, cursor: "pointer", fontSize: 12.5,
                               background: "var(--oh-bg-elevated)",
                               color: "var(--oh-text)",
                               border: "1px solid var(--oh-border)" }}>
                <span style={{ fontWeight: 500 }}>
                  {dateFR((c.cree_le || "").slice(0, 10))}
                </span>
                {" — "}
                {/* 🔑 CE QUI PERMET DE RECONNAÎTRE LE SIEN. Une date seule ne
                    suffit pas : les trois brouillons du 06 sont du même jour. */}
                {c.caracteres_texte
                  ? `${c.caracteres_texte} caractères de texte complémentaire`
                  : "aucun texte complémentaire"}
                {c.nb_destinataires
                  ? ` · ${c.nb_destinataires} destinataire(s)`
                  : " · aucun destinataire"}
              </button>
            ))}
          </div>
        </div>
      )}

      {!brouillon ? (
        <>
          {/* 🔴 L'ORDRE SUIT LE GESTE MÉTIER. Le champ « Destinataire (à) »
              était ICI, en PREMIER — on saisissait une adresse avant que le
              pli existe, avant même de savoir ce qu'il couvrirait. Or le
              périmètre décide du sujet ET du corps ; l'adresse, elle, dépend
              du territoire retenu. Choisir d'abord à QUI, puis découvrir QUOI,
              c'est l'ordre inverse du raisonnement.
              ⇒ On arrête le périmètre, le brouillon NAÎT, PUIS on choisit les
              destinataires dans le panneau — où l'annuaire peut enfin proposer,
              puisqu'il connaît alors les départements du pli.
              ⚠️ Le serveur n'exige l'adresse qu'à l'ENVOI (`brouillons.py`,
              « aucun destinataire ») : la garde reste où elle compte, ⛔ elle
              n'est pas déplacée, seulement le moment de la SAISIE. */}
          {/* 🔴 #826 — LE PÉRIMÈTRE SE COCHE **AVANT** LA COMPOSITION.
              Il était choisi à l'envoi, après que le sujet et le corps étaient
              déjà écrits : le 78 et le 94 étaient consignés et JAMAIS annoncés.
              Désormais il entre ici, et tout le pli en découle.
              ⛔ Rien n'est pré-coché. */}
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8,
                        background: "var(--oh-stat-amber-bg)",
                        color: "var(--oh-stat-amber-text)" }}
               data-testid="brouillon-perimetre-choix">
            <strong style={{ fontWeight: 500, fontSize: 13 }}>
              Que couvre RÉELLEMENT ce pli ?
            </strong>
            <p style={{ fontSize: 12, margin: "4px 0 8px", lineHeight: 1.6 }}>
              Chaque territoire a son propre résultat : une délégation peut
              accepter quand une autre refuse. Cochez les demandes réellement
              couvertes — elles seront <strong>écrites dans l&apos;objet et dans
              le corps</strong> du message.
              <br />
              Seules celles adressées à <strong>{pack.destinataire}</strong> sont
              proposées : un pli ne s&apos;adresse qu&apos;à une administration.
            </p>
            {options.length === 0 && (
              <p style={{ fontSize: 12, margin: 0 }}>Aucune demande disponible.</p>
            )}
            {options.map((o) => (
              <label key={o.code} style={{ display: "flex", alignItems: "center",
                                           gap: 8, fontSize: 13, padding: "2px 0",
                                           cursor: "pointer" }}>
                <input type="checkbox" checked={perimetre.includes(o.code)}
                       data-testid={`brouillon-perimetre-${o.code}`}
                       onChange={(e) => setPerimetre((prev) => e.target.checked
                         ? [...prev, o.code] : prev.filter((v) => v !== o.code))} />
                {o.libelle}
              </label>
            ))}
          </div>

          {/* 🔴 #796 — SOUS QUELLE FORME LE PLI PART. Le nominal est coché ;
              l'archive reste possible, mais elle se CHOISIT explicitement. */}
          <p style={{ ...labelStyle, marginTop: 12 }}>Forme du pli</p>
          <div style={{ display: "grid", gap: 6, marginTop: 4 }}
               data-testid="brouillon-mode-envoi">
            {[
              { v: "pieces_separees",
                t: "Pièces jointes séparées",
                d: "Recommandé — un fichier par pièce, plus le bordereau. C'est la forme des envois qui ont abouti, et certaines passerelles administratives rejettent les archives." },
              { v: "archive",
                t: "Une archive ZIP",
                d: "Un seul fichier contenant toutes les pièces. ⚠️ Peut être filtré par l'antivirus du destinataire, sans que nous en soyons informés." },
            ].map((o) => (
              <label key={o.v}
                     style={{ display: "flex", gap: 8, alignItems: "flex-start",
                              padding: 10, borderRadius: 8, cursor: "pointer",
                              maxWidth: 560,
                              border: `0.5px solid ${mode === o.v ? "var(--oh-accent)" : "var(--oh-border)"}`,
                              background: mode === o.v ? "var(--oh-accent-bg)" : "transparent" }}>
                <input type="radio" name={`mode-${pack.code}`} value={o.v}
                       checked={mode === o.v} onChange={() => setMode(o.v)}
                       style={{ marginTop: 2 }}
                       data-testid={`brouillon-mode-${o.v}`} />
                <span>
                  <span style={{ fontSize: 13, color: "var(--oh-text)" }}>{o.t}</span>
                  <span style={{ display: "block", fontSize: 12,
                                 color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
                    {o.d}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div style={{ marginTop: 12 }}>
            {/* 🔴 CE BOUTON ÉTAIT INERTE ET MUET. Il porte le MÊME libellé que
                celui qui a ouvert cet encadré : on croit avoir déjà cliqué,
                on reclique, rien ne se passe, et rien ne dit pourquoi. Le
                blocage est légitime (⛔ un pli n'annonce pas un périmètre
                vide) — c'est le SILENCE qui ne l'était pas.
                ⚠️ Le libellé change aussi : « Créer le brouillon » dit ce qui
                va se produire, et ⛔ ne répète plus le bouton précédent. */}
            <BoutonPrimaire onClick={preparer}
                            disabled={occupe === "preparer" || perimetre.length === 0}
                            data-testid={`habilitation-preparer-${pack.code}`}>
              {occupe === "preparer" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Créer le brouillon
            </BoutonPrimaire>
            <BoutonDiscret style={{ marginLeft: 8, height: 36 }} onClick={onFerme}>
              Annuler
            </BoutonDiscret>
            {perimetre.length === 0 && (
              <p style={{ ...labelStyle, marginTop: 8 }}
                 data-testid={`habilitation-preparer-motif-${pack.code}`}>
                ⚠️ Cochez d&apos;abord au moins une demande ci-dessus : le sujet
                et le corps du message en DÉCOULENT, et le pli annonce ce
                périmètre au destinataire.
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          {/* 🔴 #782 — LA RAISON EST DITE ICI, ⛔ pas découverte après le clic.
              Le brouillon PORTE déjà `pret_a_envoyer` et `motif_non_envoyable` :
              ne pas les afficher, c'était laisser JJ cliquer pour apprendre. */}
          {brouillon.pret_a_envoyer === false && (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 8, fontSize: 12.5,
                          lineHeight: 1.6, background: "var(--oh-stat-red-bg)",
                          color: "var(--oh-stat-red-text)" }}
                 data-testid="brouillon-non-pret">
              <strong style={{ fontWeight: 500 }}>
                <AlertTriangle size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                Ce pack ne peut pas être envoyé en l&apos;état
              </strong>
              <p style={{ margin: "4px 0 0" }}>{brouillon.motif_non_envoyable}</p>
              <p style={{ margin: "6px 0 0", fontSize: 12 }}>
                Vous pouvez tout de même relire le brouillon ci-dessous : le
                destinataire, le corps et les pièces sont ceux qui partiraient.
              </p>
            </div>
          )}

          {/* ⚠️ CE QUI PART, TEL QU'IL PARTIRA — ⛔ pas une description */}
          <div style={{ display: "grid", gap: 6, fontSize: 13, color: "var(--oh-text)" }}>
            {/* 🔴 TOUTES les adresses principales, ⛔ plus seulement la
                première. Cette ligne lisait `destinataire_email`, que le
                serveur maintient à `to[0]` : un pli parti à trois s'affichait
                « À une-seule-adresse ». L'écran doit dire ce qui part. */}
            <div><span style={labelStyle}>À </span>
              {((brouillon.destinataires || {}).to || []).join(", ")
               || brouillon.destinataire_email || "—"}
              <span style={{ color: "var(--oh-text-muted)" }}> · {brouillon.destinataire}</span></div>
            <div><span style={labelStyle}>Objet </span>{brouillon.sujet}</div>
          </div>

          {repris && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 8,
                          fontSize: 12.5, lineHeight: 1.6,
                          background: "var(--oh-info-bg)",
                          color: "var(--oh-info-text)" }}
                 data-testid="brouillon-repris">
              <strong style={{ fontWeight: 500 }}>Brouillon repris</strong> —
              préparé le {dateFR((brouillon.cree_le || "").slice(0, 10))}. Son
              périmètre, ses destinataires et son texte sont ceux que vous aviez
              saisis. Pour repartir d&apos;un périmètre différent, abandonnez
              celui-ci d&apos;abord — sinon deux brouillons coexisteraient et
              vous reliriez le mauvais.
            </div>
          )}

          {/* 🔴 L'ÉCRAN D'ENVOI — destinataires, annuaire, texte complémentaire.
              L'ancien champ « Copie (CC) » a été RETIRÉ : il partait par le
              paramètre d'envoi, qui s'ADDITIONNE aux destinataires du
              brouillon. Garder les deux aurait doublé les copies dès qu'un
              opérateur aurait rempli l'un et l'autre. Une donnée, une source. */}
          <DestinatairesPanel brouillon={brouillon}
                              onBrouillonChange={setBrouillon}
                              onErreur={onErreur} />

          {/* 🔑 LE CORPS EST AFFICHÉ APRÈS le panneau, et c'est celui que le
              serveur vient de RECOMPOSER — ⛔ pas une prévisualisation locale. */}
          <p style={{ ...labelStyle, marginTop: 12 }}>Corps du message — tel qu'il partira</p>
          <pre style={{ fontSize: 12, color: "var(--oh-text-secondary)", lineHeight: 1.6,
                        // ⚠️ 190 px montraient six lignes d'un texte qu'on
                        // demande de RELIRE avant un envoi sans retour. Un
                        // pavé qu'on ne peut pas lire d'un coup se fait
                        // survoler, et la relecture devient un clic.
                        whiteSpace: "pre-wrap", margin: 0, maxHeight: 360, overflow: "auto",
                        padding: 10, borderRadius: 8, background: "var(--oh-bg-elevated)",
                        border: "0.5px solid var(--oh-border)" }}>{brouillon.corps}</pre>

          {/* 🔑 LES PIÈCES FIGÉES — celles de l'archive RÉELLE, ⛔ pas les exigences */}
          {/* ⚠️ #796 — LA FORME EST RAPPELÉE ICI, avec le poids réel. « archive
              X.zip » affiché sur un envoi en pièces séparées ferait croire à JJ
              qu'un ZIP part, alors que N fichiers partent. */}
          <p style={{ ...labelStyle, marginTop: 12 }}>
            Pièces jointes — {brouillon.nb_pieces_jointes ?? brouillon.nb_pieces}
            {brouillon.mode_envoi === "archive"
              ? ` · une archive : ${brouillon.nom_archive}`
              : " · fichiers séparés, bordereau compris"}
            {brouillon.poids
              ? ` · ${Math.round((brouillon.poids.poids_total_octets || 0) / 1024)} Ko`
              : ""}
          </p>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12,
                       color: "var(--oh-text-secondary)", lineHeight: 1.6,
                       maxHeight: 150, overflow: "auto" }}
              data-testid="brouillon-pieces">
            {(brouillon.pieces_jointes || []).map((p) => (
              <li key={p.fichier}>{p.libelle} — v{p.version}</li>
            ))}
          </ul>
          {/* 🔑 L'EMPREINTE AFFICHÉE EST CELLE QUI EXISTE. En pièces séparées il
              n'y a pas d'archive : afficher `empreinte_archive` rendrait un
              vide, ou pire, la trace d'un ZIP qui ne part pas. */}
          <p style={{ fontSize: 11, color: "var(--oh-text-muted)", marginTop: 4 }}>
            empreinte{" "}
            {brouillon.mode_envoi === "archive" ? "de l'archive " : "de l'ensemble "}
            {String(brouillon.empreinte_archive
                    || brouillon.empreinte_ensemble || "").slice(0, 16)}…
          </p>

          {/* 🔴 #826 — LE PÉRIMÈTRE NE SE CHOISIT PLUS ICI : il est FIGÉ.
              Il a été coché AVANT la préparation, et le sujet comme le corps
              ci-dessus en découlent. ⛔ Le laisser modifiable ici rétablirait
              exactement le défaut du #825 — un territoire consigné que le pli
              n'annonce pas. Pour en changer : on re-prépare. */}
          <div style={{ marginTop: 14, padding: 12, borderRadius: 8,
                        background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}
               data-testid="brouillon-perimetre-fige">
            <strong style={{ fontWeight: 500, fontSize: 13 }}>
              Ce pli couvre — et il le DIT au destinataire
            </strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
              {(brouillon.perimetre_annonce || []).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            <p style={{ fontSize: 12, margin: "8px 0 0", lineHeight: 1.6 }}>
              Ces lignes sont celles du corps du message ci-dessus : la même
              donnée sert à l&apos;annonce et à la preuve. Pour changer le
              périmètre, préparez un nouveau brouillon.
            </p>
          </div>

          {/* 🔴 LA NATURE — ⛔ aucun défaut. Un essai et un pli administratif ne
              se devinent pas, et les confondre fausserait l'état du dossier. */}
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8,
                        background: "var(--oh-stat-amber-bg)",
                        color: "var(--oh-stat-amber-text)" }}
               data-testid="brouillon-nature">
            <strong style={{ fontWeight: 500, fontSize: 13 }}>
              Quelle est la nature de cet envoi ?
            </strong>
            {[
              { v: "canary", t: "Essai (canary)",
                d: "Vers une adresse à vous. Consigné avec sa composition et son empreinte, mais ⛔ ne fait pas passer le dossier à « transmis » et ⛔ ne déclenche pas la garde anti-second-envoi." },
              { v: "administratif", t: "Pli administratif réel",
                d: "Adressé à l'administration. Il engage le dossier et fait foi." },
            ].map((o) => (
              <label key={o.v} style={{ display: "flex", gap: 8, alignItems: "flex-start",
                                        marginTop: 8, cursor: "pointer" }}>
                <input type="radio" name={`nature-${pack.code}`} value={o.v}
                       checked={nature === o.v} onChange={() => setNature(o.v)}
                       style={{ marginTop: 3 }}
                       data-testid={`brouillon-nature-${o.v}`} />
                <span>
                  <span style={{ fontSize: 13 }}>{o.t}</span>
                  <span style={{ display: "block", fontSize: 12, lineHeight: 1.6 }}>
                    {o.d}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {alerte && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, fontSize: 12.5,
                          lineHeight: 1.6, background: "var(--oh-stat-red-bg)",
                          color: "var(--oh-stat-red-text)" }}
                 data-testid="brouillon-alerte">
              {alerte}
              <label style={{ display: "flex", alignItems: "center", gap: 8,
                              marginTop: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={confirmer}
                       data-testid="brouillon-confirmer"
                       onChange={(e) => setConfirmer(e.target.checked)} />
                Je confirme ce renvoi volontaire
              </label>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <BoutonPrimaire onClick={envoyer}
                            disabled={occupe === "envoyer" || !nature
                                      || brouillon.pret_a_envoyer === false}
                            data-testid={`habilitation-valider-envoi-${pack.code}`}>
              {occupe === "envoyer" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Valider et envoyer
            </BoutonPrimaire>
            <BoutonDiscret style={{ height: 36 }} onClick={abandonner}
                           data-testid="brouillon-abandonner">
              Abandonner
            </BoutonDiscret>
            {/* ⚠️ #782 — UN BOUTON GRISÉ QUI DIT POURQUOI INFORME ; un bouton
                absent ne dit rien. C'est la leçon du #756. */}
            <span style={{ fontSize: 12, color: "var(--oh-text-muted)",
                           alignSelf: "center" }}>
              {brouillon.pret_a_envoyer === false
                ? "Envoi impossible tant que les pièces obligatoires manquent."
                : perimetre.length === 0
                  ? "Cochez au moins une demande couverte."
                  : ""}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function DetailPack({ pack, packs, onErreur, onEnvoiConsigne, onRafraichir }) {
  const [brouillonOuvert, setBrouillonOuvert] = useState(false);
  const [occupe, setOccupe] = useState(null);

  const action = async (cle, fn) => {
    setOccupe(cle);
    try { await fn(); } catch (e) { onErreur(e.message); } finally { setOccupe(null); }
  };

  // ⚠️ GÉNÉRER et RÉGÉNÉRER sont le MÊME appel — c'est le versionnage côté
  // serveur qui fait la différence. ⛔ Deux chemins pour un seul geste
  // finiraient par diverger.
  // ⚠️ Puis on RECHARGE : la vérité est en base, pas dans l'état de l'écran.
  const generer = async (typePiece, ecran, reprojetable) => {
    // ⚠️ #716 — une pièce qui porte son SNAPSHOT se REPROJETTE sur place : les
    // saisies sont déjà figées dedans. ⛔ Repasser par l'écran ne servirait à
    // rien, et RELIRE la base ferait bouger des chiffres déjà arrêtés.
    if (reprojetable) {
      await genererPiece(typePiece, "reprojeter");
      await onRafraichir();
      return;
    }
    // ⚠️ Certaines pièces ne se produisent PAS d'un clic : le bilan AMO exige la
    // présidence, les effectifs et les textes éditoriaux, que porte son propre
    // écran. On y CONDUIT — ⛔ générer ici produirait un document amputé, et le
    // gabarit omet les rubriques vides SANS RIEN DIRE.
    if (ecran) {
      window.dispatchEvent(new CustomEvent("navigate",
        { detail: { hub: "management", item: ecran } }));
      return;
    }
    await genererPiece(typePiece);
    await onRafraichir();
  };

  const obligatoiresManquantes = (pack.manquantes || []).filter((m) => m.obligatoire);

  return (
    <div style={{ marginTop: 12 }}>
      {obligatoiresManquantes.length > 0 && (
        <div
          style={{
            background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)",
            borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, lineHeight: 1.6,
          }}
          data-testid={`habilitation-manquantes-${pack.code}`}
        >
          <strong style={{ fontWeight: 500 }}>
            <AlertTriangle size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            {obligatoiresManquantes.length} pièce(s) manquante(s)
          </strong>
          {/* ⚠️ La liste était MORTE : elle nommait le manque sans offrir le
              geste. Une exigence que le produit sait établir se règle ici, en un
              clic — ⛔ inutile d'aller chercher ailleurs ce qu'OmniHub produit. */}
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {/* ⚠️ #782 — le bouton était INLINE, donc posé au milieu du texte de
                la ligne quand celle-ci passait à la ligne. Il descend sur sa
                propre ligne : une action ne se lit pas au milieu d'une phrase. */}
            {obligatoiresManquantes.map((m) => (
              <li key={m.type_piece} style={{ marginBottom: m.generable ? 8 : 0 }}>
                <span style={{ display: "block" }}>{m.libelle} — {m.motif}</span>
                {m.generable && (
                  <BoutonDiscret
                    style={{ marginTop: 4, height: 26, fontSize: 12 }}
                    disabled={occupe === `gen-${m.type_piece}`}
                    onClick={() => action(`gen-${m.type_piece}`,
                                          () => generer(m.type_piece, m.generation_ecran))}
                    data-testid={`habilitation-generer-${m.type_piece}`}
                  >
                    {occupe === `gen-${m.type_piece}`
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Sparkles size={12} />}
                    {m.generation_ecran ? "Générer le rapport" : "Générer"}
                  </BoutonDiscret>
                )}
              </li>
            ))}
          </ul>
          <p style={{ margin: "8px 0 0", fontSize: 12 }}>
            Le pack se télécharge quand même : la liste jointe n&apos;annoncera que
            les pièces réellement présentes.
          </p>
        </div>
      )}

      {(pack.non_valides || []).length > 0 && (
        <div
          style={{
            background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)",
            borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, lineHeight: 1.6,
          }}
          data-testid={`habilitation-non-valides-${pack.code}`}
        >
          <strong style={{ fontWeight: 500 }}>
            {pack.non_valides.length} pièce(s) présente(s) mais périmée(s)
          </strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {pack.non_valides.map((p) => (
              <li key={p.type_piece}>
                {p.libelle}
                {p.date_expiration ? ` — expirée le ${dateFR(p.date_expiration)}` : ""}
              </li>
            ))}
          </ul>
          <p style={{ margin: "8px 0 0", fontSize: 12 }}>
            Elles restent jointes au pli — une pièce ne disparaît pas parce
            qu&apos;elle est périmée. Mais le pack ne sera « prêt à envoyer »
            qu&apos;une fois remplacées par une version valide.
          </p>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <BoutonPrimaire
          disabled={occupe === "zip"}
          onClick={() => action("zip", () =>
            telecharger(`/api/metier/habilitation/packs/${pack.code}/archive`, `${pack.code}.zip`))}
          data-testid={`habilitation-zip-${pack.code}`}
        >
          {occupe === "zip" ? <Loader2 size={15} className="animate-spin" /> : <Archive size={15} />}
          Télécharger le pack complet
        </BoutonPrimaire>

        <BoutonSecondaire
          disabled={occupe === "bordereau"}
          onClick={() => action("bordereau", () =>
            telecharger(`/api/metier/habilitation/packs/${pack.code}/bordereau`, `${pack.code}.pdf`))}
          data-testid={`habilitation-bordereau-${pack.code}`}
        >
          {occupe === "bordereau" ? <Loader2 size={15} className="animate-spin" /> : <ClipboardList size={15} />}
          Liste des pièces jointes
        </BoutonSecondaire>

        {/* 🔴 #781 — LE CHEMIN D'ENVOI RÉEL. « Consigner » (à côté) reste le
            canal historique : JJ adresse le pli lui-même et le déclare. Ici,
            c'est OmniHub qui transmet — d'où la relecture obligatoire. */}
        <BoutonSecondaire
          onClick={() => setBrouillonOuvert((v) => !v)}
          data-testid={`habilitation-ouvrir-brouillon-${pack.code}`}
        >
          <Send size={15} />
          {brouillonOuvert ? "Fermer le brouillon" : "Préparer l'envoi"}
        </BoutonSecondaire>

        <BoutonDiscret
          disabled={occupe === "envoi"} style={{ height: 36 }}
          onClick={() => action("envoi", () => onEnvoiConsigne(pack))}
          data-testid={`habilitation-envoi-${pack.code}`}
        >
          {occupe === "envoi" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Consigner l&apos;envoi
        </BoutonDiscret>
      </div>

      {brouillonOuvert && (
        <BrouillonEnvoi
          pack={pack} packs={packs} onErreur={onErreur}
          onFerme={() => setBrouillonOuvert(false)}
          onEnvoye={async (rendu) => {
            setBrouillonOuvert(false);
            await onRafraichir();
            // 🔴 #787 — ⛔ NE JAMAIS LAISSER CROIRE QU'UN PLI EST PARTI.
            // En preprod, `egress_guard` neutralise la sortie : la transmission
            // est consignée « envoyé » avec `message_id: "preprod-skipped"` et
            // RIEN N'EST SORTI. ⚠️ Dire « Transmission consignée » sans le
            // préciser laisserait JJ croire que l'administration a reçu le pli.
            const neutralise = rendu.resultat_envoi?.skipped_preprod === true
              || rendu.resultat_envoi?.message_id === "preprod-skipped";
            window.alert(
              (neutralise
                ? "⛔ RIEN N'EST PARTI — environnement de test.\n\n"
                  + "La transmission est consignée et le parcours est allé au "
                  + "bout, mais la sortie est neutralisée sur cet environnement : "
                  + "AUCUN message n'a quitté OmniHub, et l'administration n'a "
                  + "rien reçu.\n\n"
                : "Transmission consignée — le message a été remis au serveur "
                  + "d'envoi.\n\n") +
              `Identifiant : ${rendu.transmission_id}\n` +
              `Périmètre : ${(rendu.perimetre_metier || []).join(", ")}\n` +
              `Pièces : ${rendu.nb_pieces}\n` +
              `Référence du message : ${rendu.resultat_envoi?.message_id || "—"}` +
              (neutralise ? "  (jeton de test, ⛔ pas un envoi réel)" : ""));
          }} />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--oh-text)", margin: "0 0 8px" }}>
            {/* 🔴 #673 — ⛔ PLUS DE `nb_presentes/nb_attendues` : ce ratio
                MÉLANGE obligatoires et complémentaires, et affichait 3/13 là
                où le dossier est à 3/11. Une complémentaire absente n'a jamais
                empêché un envoi : la compter dans le même dénominateur fait
                croire à un manque qui n'existe pas. */}
            Pièces obligatoires ({pack.obligatoires_presentes}/{pack.obligatoires_attendues})
          </h3>
          {(pack.presentes || []).length === 0 && (
            <p style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>Aucune.</p>
          )}
          {(pack.presentes || []).map((p) => (
            <div key={p.type_piece}
                 style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                          gap: 8, padding: "6px 0", borderBottom: "0.5px solid var(--oh-border)" }}>
              <span style={{ fontSize: 13, color: "var(--oh-text)" }}>
                {p.libelle}
                <span style={{ fontSize: 11, color: "var(--oh-text-muted)", marginLeft: 6 }}>
                  v{p.version} · {p.fichier_nom}
                </span>
                {/* ⚠️ D'OÙ VIENT LA PIÈCE, DIT À L'ÉCRAN. Sans cette ligne, une
                    pièce produite par OmniHub passe pour un dépôt manuel : on
                    irait la redemander dehors au lieu de la régénérer. La date
                    d'arrêt et le périmètre sont ce qui permet de juger si elle
                    est encore à jour. */}
                {p.genere_automatiquement && (
                  <span style={{ fontSize: 11, color: "var(--oh-text-muted)", display: "block" }}>
                    {p.source_libelle || "généré par OmniHub"}
                    {p.generation?.date_arrete && ` · arrêté au ${dateFR(p.generation.date_arrete)}`}
                    {p.generation?.perimetre?.departements?.length
                      ? ` · départements ${p.generation.perimetre.departements.join(" · ")}`
                      : ""}
                    {p.generation?.conclusion === "neant" && " · conclusion : néant"}
                  </span>
                )}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge ton={TONS_STATUT[p.statut] || "blue"}>
                  {LIBELLES_STATUT[p.statut] || p.statut}
                </Badge>
                <BoutonDiscret
                  onClick={() => voir(p.telechargement, onErreur)}
                  aria-label={`Voir ${p.libelle}`}
                >
                  <Eye size={14} />
                  Voir
                </BoutonDiscret>
                <BoutonDiscret
                  onClick={() => telecharger(p.telechargement, p.fichier_nom).catch((e) => onErreur(e.message))}
                  aria-label={`Télécharger ${p.libelle}`}
                >
                  <FileDown size={14} />
                </BoutonDiscret>
                {/* ⚠️ RÉGÉNÉRER, ⛔ pas « remplacer » : la v1 est conservée. Une
                    pièce déjà citée dans un envoi historique ne bouge jamais. */}
                {p.generable && (
                  <BoutonDiscret
                    disabled={occupe === `gen-${p.type_piece}`}
                    onClick={() => action(`gen-${p.type_piece}`,
                                          () => generer(p.type_piece, p.generation_ecran,
                                                        p.reprojetable))}
                    data-testid={`habilitation-regenerer-${p.type_piece}`}
                    aria-label={`Régénérer ${p.libelle}`}
                  >
                    {occupe === `gen-${p.type_piece}`
                      ? <Loader2 size={14} className="animate-spin" />
                      : <RefreshCw size={14} />}
                    Régénérer
                  </BoutonDiscret>
                )}
              </span>
            </div>
          ))}
        </div>

        <div>
          <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--oh-text)", margin: "0 0 8px" }}>
            {/* ⚠️ #860 — CE N'EST PLUS « l'historique d'envoi ». Ce champ
                consigne les plis partis AUTREMENT (courrier, dépôt) ; le lire
                comme la preuve d'un envoi OmniHub était l'autre moitié du
                défaut du #856. */}
            Plis consignés manuellement
          </h3>
          {(pack.historique_envois || []).length === 0 && (
            <p style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>
              Aucun envoi consigné.
            </p>
          )}
          {/* ⛔ HISTORIQUE, pas état courant : chaque entrée est présentée AVEC SA
              DATE et porte les versions réellement transmises. Rien n'y est
              recalculé — un pack archivé atteste de ce qui est parti. */}
          {(pack.historique_envois || []).slice().reverse().map((e, i) => (
            <details key={i} style={{ padding: "6px 0", borderBottom: "0.5px solid var(--oh-border)" }}>
              <summary style={{ fontSize: 13, color: "var(--oh-text)", cursor: "pointer" }}>
                Envoi du {dateFR(e.date)} — {e.nb_pieces} pièce(s) · {e.canal}
              </summary>
              <span style={{ fontSize: 11, color: "var(--oh-text-muted)", display: "block", marginTop: 4 }}>
                empreinte {String(e.empreinte_archive
                                  || e.empreinte_ensemble || "").slice(0, 12)}…
                {/* ⚠️ #796 — la forme n'est dite QUE si elle est connue. Cet
                    historique-ci consigne aussi des plis partis AUTREMENT
                    (courrier, dépôt) : y écrire « archive » par défaut serait
                    affirmer une forme que personne n'a enregistrée. */}
                {e.mode_envoi
                  ? ` · ${e.mode_envoi === "pieces_separees" ? "pièces séparées" : "archive"}`
                  : ""}
                {e.par ? ` · ${e.par}` : ""}
                {e.nb_manquantes_au_depart
                  ? ` · ${e.nb_manquantes_au_depart} manquante(s) au départ`
                  : ""}
                {e.note ? ` · ${e.note}` : ""}
              </span>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12,
                           color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
                {(e.composition || []).map((c) => (
                  <li key={c.fichier_archive}>
                    {c.libelle} — v{c.version}
                    {c.statut_au_depart === "perime" ? " (périmée au départ)" : ""}
                  </li>
                ))}
                {!(e.composition || []).length && (
                  <li>Composition non consignée pour cet envoi.</li>
                )}
              </ul>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Écran
// ─────────────────────────────────────────────────────────────────────────────

// =============================================================================
//  SUIVI DES DÉMARCHES (#863) — 🔑 TOUT EST PROJETÉ, ⛔ RIEN N'EST STOCKÉ
// =============================================================================
// L'écran lit : l'IDENTITÉ issue du référentiel + le JOURNAL → la PROJECTION.
// ⛔ AUCUN champ de statut persistant n'est créé, ni lu, ni écrit ici.
//
// 🔴 CE QUI DOIT TENIR : le 78 peut devenir « habilitation accordée » PENDANT
// que le 94 devient « habilitation refusée ». Une ligne PAR DÉMARCHE, ⛔ jamais
// un verdict unique par pack — « le 94 a choisi de ne pas répondre
// favorablement » (DRIHL).
// =============================================================================
const TONS_EVENEMENT = {
  envoi_effectue: "blue",
  accuse_reception: "cyan",
  reponse_recue: "cyan",
  complement_demande: "amber",
  complement_envoye: "blue",
  instruction_en_cours: "purple",
  habilitation_accordee: "green",
  habilitation_refusee: "red",
  relance_envoyee: "amber",
};

// ⚠️ CE QUE JJ DOIT FAIRE ENSUITE — dérivé de l'état, ⛔ jamais stocké.
// ⛔ Aucune échéance n'est suggérée ici : elle vient de la preuve, ou elle
// n'existe pas (#860).
const PROCHAINE_ACTION = {
  null: "Préparer et envoyer le pli",
  envoi_effectue: "Attendre l'accusé de réception",
  accuse_reception: "Attendre l'instruction",
  reponse_recue: "Lire la réponse et consigner la suite",
  complement_demande: "Réunir et envoyer le complément",
  complement_envoye: "Attendre l'instruction",
  instruction_en_cours: "Attendre la décision",
  relance_envoyee: "Attendre la réponse à la relance",
  habilitation_accordee: "Aucune — décision rendue",
  habilitation_refusee: "Aucune — décision rendue",
};

function EcheanceBadge({ etat }) {
  // ⚠️ #860 — une échéance n'existe QUE si le délai figurait dans la preuve.
  // ⛔ Rien n'est calculé : pas d'échéance ⇒ on n'affiche PAS de tiret alarmant,
  // on n'affiche rien.
  if (!etat.echeance) return null;
  const restant = Math.ceil(
    (new Date(etat.echeance) - new Date()) / 86400000);
  const ton = restant < 0 ? "red" : restant <= 7 ? "amber" : "blue";
  return (
    <Badge ton={ton} titre={etat.source_echeance || "Source non précisée"}>
      Échéance {dateFR(etat.echeance)}
      {restant < 0 ? ` · dépassée de ${-restant} j` : ` · dans ${restant} j`}
    </Badge>
  );
}

function Timeline({ etat }) {
  const faits = etat.journal || [];
  if (!faits.length) {
    return (
      <p style={{ fontSize: 12.5, color: "var(--oh-text-secondary)", margin: 0 }}>
        Aucun événement consigné. Le journal se remplira à partir du premier pli
        transmis, puis des réponses de l&apos;administration.
      </p>
    );
  }
  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0 }}
        data-testid={`demarche-timeline-${etat.demarche_code}`}>
      {faits.map((f, i) => (
        <li key={f.evenement_id}
            style={{ display: "flex", gap: 10, padding: "8px 0",
                     borderTop: i ? "0.5px solid var(--oh-border)" : "none" }}>
          <span style={{ minWidth: 86, fontSize: 12, color: "var(--oh-text-muted)" }}>
            {dateFR(f.date_evenement)}
          </span>
          <span style={{ flex: 1 }}>
            <Badge ton={TONS_EVENEMENT[f.type] || "blue"}>
              {LIBELLES_EVENEMENT[f.type] || f.type}
            </Badge>
            {f.motif && (
              <span style={{ fontSize: 12.5, color: "var(--oh-text-secondary)",
                             display: "block", marginTop: 4, lineHeight: 1.6 }}>
                {f.motif}
              </span>
            )}
            <span style={{ fontSize: 11, color: "var(--oh-text-muted)",
                           display: "block", marginTop: 3 }}>
              {/* ⚠️ #860 — un événement DÉRIVÉ n'a pas été saisi : le dire, pour
                  qu'on ne cherche pas qui l'a consigné. */}
              {f.origine === "derive_transmission"
                ? "dérivé de la transmission — ⛔ aucune saisie"
                : `constaté par ${f.constate_par || "—"}`}
              {f.echeance ? ` · échéance ${dateFR(f.echeance)}` : ""}
              {f.reference_administration ? ` · réf. ${f.reference_administration}` : ""}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

const LIBELLES_EVENEMENT = {
  envoi_effectue: "Envoi effectué",
  accuse_reception: "Accusé de réception",
  reponse_recue: "Réponse reçue",
  complement_demande: "Complément demandé",
  complement_envoye: "Complément envoyé",
  instruction_en_cours: "En instruction",
  habilitation_accordee: "Habilitation accordée",
  habilitation_refusee: "Habilitation refusée",
  relance_envoyee: "Relance envoyée",
};

function OngletDemarches() {
  const [etats, setEtats] = useState([]);
  const [propositions, setPropositions] = useState([]);
  const [ouverte, setOuverte] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let vivant = true;
    (async () => {
      setChargement(true); setErreur(null);
      try {
        const [dR, pR] = await Promise.all([
          fetch(`${API}/api/metier/habilitation/demarches`, { credentials: "include" }),
          fetch(`${API}/api/metier/habilitation/demarches/propositions`,
                { credentials: "include" }),
        ]);
        if (!dR.ok) throw new Error(`Chargement impossible (${dR.status})`);
        const d = await dR.json();
        const p = pR.ok ? await pR.json() : { propositions: [] };
        if (!vivant) return;
        setEtats(d.demarches || []);
        setPropositions(p.propositions || []);
      } catch (e) {
        if (vivant) setErreur(e.message);
      } finally {
        if (vivant) setChargement(false);
      }
    })();
    return () => { vivant = false; };
  }, []);

  if (chargement) {
    return (
      <p style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>
        <Loader2 size={14} className="animate-spin" style={{ verticalAlign: "-2px" }} />
        {" "}Projection des démarches…
      </p>
    );
  }
  if (erreur) {
    return (
      <div style={{ padding: 12, borderRadius: 8, fontSize: 12.5,
                    background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>
        {erreur}
      </div>
    );
  }

  return (
    <div data-testid="habilitation-demarches">
      <p style={{ fontSize: 12.5, color: "var(--oh-text-secondary)",
                  lineHeight: 1.6, margin: "0 0 14px" }}>
        Chaque territoire a <strong>son propre résultat</strong> : une délégation
        peut accorder l&apos;habilitation pendant qu&apos;une autre la refuse.
        L&apos;état ci-dessous est <strong>calculé</strong> à partir des plis
        transmis et des événements consignés — il n&apos;est stocké nulle part.
      </p>

      {/* ⚠️ AG-04 n'est pas branché : le dire PROPREMENT, ⛔ pas laisser une
          zone morte qui ferait croire à une panne. */}
      {propositions.length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--oh-text-muted)",
                    margin: "0 0 14px", lineHeight: 1.6 }}
           data-testid="demarches-propositions-vide">
          Aucune proposition automatique : l&apos;agent de lecture des courriers
          n&apos;est pas encore raccordé. Les événements se consignent à la main,
          et c&apos;est voulu — une proposition devra de toute façon être validée
          par un humain avant d&apos;entrer au journal.
        </p>
      ) : (
        <div style={{ marginBottom: 14, padding: 12, borderRadius: 8,
                      background: "var(--oh-stat-amber-bg)",
                      color: "var(--oh-stat-amber-text)" }}
             data-testid="demarches-propositions">
          <strong style={{ fontWeight: 500, fontSize: 13 }}>
            {propositions.length} proposition(s) en attente de validation
          </strong>
          <p style={{ fontSize: 12, margin: "4px 0 0", lineHeight: 1.6 }}>
            ⛔ Elles ne sont pas au journal : un humain doit les valider.
          </p>
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--oh-text-secondary)",
                         fontSize: 12 }}>
              <th style={{ padding: "6px 8px" }}>Territoire</th>
              <th style={{ padding: "6px 8px" }}>Démarche</th>
              <th style={{ padding: "6px 8px" }}>État projeté</th>
              <th style={{ padding: "6px 8px" }}>Dernier événement</th>
              <th style={{ padding: "6px 8px" }}>Destinataire</th>
              <th style={{ padding: "6px 8px" }}>Prochaine action</th>
              <th style={{ padding: "6px 8px" }} />
            </tr>
          </thead>
          <tbody>
            {etats.map((e) => (
              <React.Fragment key={e.demarche_code}>
                <tr style={{ borderTop: "0.5px solid var(--oh-border)" }}
                    data-testid={`demarche-ligne-${e.demarche_code}`}>
                  <td style={{ padding: "8px", fontWeight: 500 }}>
                    {(e.territoires || []).join(" · ") || "—"}
                  </td>
                  <td style={{ padding: "8px", color: "var(--oh-text-secondary)" }}>
                    {e.libelle_demande || e.demarche_code}
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Badge ton={TONS_EVENEMENT[e.type] || "amber"}>
                      {e.libelle}
                    </Badge>
                    {/* ⚠️ Une décision reste prise même si un événement est
                        consigné après : le dire, sinon l'état courant la masque. */}
                    {e.decision_prise && e.type !== e.decision && (
                      <Badge ton={TONS_EVENEMENT[e.decision] || "blue"}
                             titre="Une décision a été rendue ; des événements ont suivi.">
                        {LIBELLES_EVENEMENT[e.decision]}
                      </Badge>
                    )}
                  </td>
                  <td style={{ padding: "8px", color: "var(--oh-text-secondary)" }}>
                    {e.date_evenement ? dateFR(e.date_evenement) : "—"}
                  </td>
                  <td style={{ padding: "8px", color: "var(--oh-text-secondary)" }}>
                    {e.destinataire || "—"}
                  </td>
                  <td style={{ padding: "8px", color: "var(--oh-text-secondary)" }}>
                    {PROCHAINE_ACTION[e.type] || PROCHAINE_ACTION.null}
                    <span style={{ display: "block", marginTop: 4 }}>
                      <EcheanceBadge etat={e} />
                    </span>
                  </td>
                  <td style={{ padding: "8px", textAlign: "right" }}>
                    <BoutonDiscret
                      onClick={() => setOuverte(
                        ouverte === e.demarche_code ? null : e.demarche_code)}
                      data-testid={`demarche-ouvrir-${e.demarche_code}`}>
                      {ouverte === e.demarche_code ? "Replier" : "Historique"}
                    </BoutonDiscret>
                  </td>
                </tr>
                {ouverte === e.demarche_code && (
                  <tr>
                    <td colSpan={7} style={{ padding: "4px 8px 16px" }}>
                      <div style={{ padding: 12, borderRadius: 8,
                                    background: "var(--oh-bg-elevated)",
                                    border: "0.5px solid var(--oh-border)" }}>
                        <p style={{ ...labelStyle, marginBottom: 8 }}>
                          Journal — {e.nb_evenements} événement(s), append-only
                        </p>
                        <Timeline etat={e} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ⛔ LE BLOC DE DIAGNOSTIC EST LAISSÉ HORS DE CE PORT. Il était gardé
          par un rôle lu dans `useAuth`, et `useAuth` ⛔ n'entre pas ici
          (contrainte JJ : il avait tenté d'entrer en contrebande le 27/08).
          ⚠️ C'est une aide au développeur, ⛔ pas la visibilité des envois que
          ce lot apporte. Il viendra avec son propre lot, s'il doit venir. */}
    </div>
  );
}

export default function HabilitationANAH() {
  const [onglet, setOnglet] = useState("packs");
  const [referentiel, setReferentiel] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [packs, setPacks] = useState([]);
  const [ouvert, setOuvert] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const [refR, bibR, packR] = await Promise.all([
        fetch(`${API}/api/metier/habilitation/referentiel`, { credentials: "include" }),
        fetch(`${API}/api/metier/habilitation/bibliotheque`, { credentials: "include" }),
        fetch(`${API}/api/metier/habilitation/packs`, { credentials: "include" }),
      ]);
      if (!refR.ok || !bibR.ok || !packR.ok) {
        throw new Error(`Chargement impossible (${refR.status}/${bibR.status}/${packR.status})`);
      }
      setReferentiel(await refR.json());
      setPieces((await bibR.json()).pieces || []);
      setPacks((await packR.json()).packs || []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const retirer = async (piece) => {
    if (!window.confirm(`Retirer « ${piece.type_libelle} » de la bibliothèque ?`)) return;
    try {
      const r = await fetch(`${API}/api/metier/habilitation/bibliotheque/${piece.id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error(`Retrait impossible (${r.status})`);
      await charger();
    } catch (e) { setErreur(e.message); }
  };

  const consignerEnvoi = async (pack) => {
    const note = window.prompt(
      `Consigner l'envoi de « ${pack.libelle} » à ${pack.destinataire}.\n` +
      "Remarque (facultatif) :", "");
    if (note === null) return;
    const r = await fetch(`${API}/api/metier/habilitation/packs/${pack.code}/envois`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: note || null, canal: "téléchargement" }),
    });
    if (!r.ok) throw new Error(`Envoi non consigné (${r.status})`);
    await charger();
  };

  const alertes = useMemo(
    () => pieces.filter((p) => ["perime", "bientot_perime"].includes(p.statut?.code)),
    [pieces]
  );

  const ongletStyle = (cle) => ({
    background: onglet === cle ? "var(--oh-accent-bg)" : "transparent",
    color: onglet === cle ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
    border: "none", borderRadius: 8, height: 36, padding: "0 14px",
    fontSize: 13, fontWeight: 500, cursor: "pointer",
  });

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 500, color: "var(--oh-text)", margin: 0 }}>
            Habilitation ANAH
          </h1>
          <p style={{ fontSize: 13, color: "var(--oh-text-secondary)", margin: "4px 0 0", lineHeight: 1.6 }}>
            Les pièces de la structure, et les demandes qui les réutilisent. Chaque
            pièce vit une seule fois : la mettre à jour la met à jour partout.
          </p>
        </div>
        <BoutonDiscret onClick={charger} disabled={chargement} aria-label="Recharger">
          {chargement ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Recharger
        </BoutonDiscret>
      </div>

      {erreur && (
        <div style={{ background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)",
                      borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
          {erreur}
        </div>
      )}

      {alertes.length > 0 && (
        <div style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)",
                      borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
          <strong style={{ fontWeight: 500 }}>
            {alertes.length} pièce(s) à renouveler avant le prochain envoi
          </strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {alertes.map((p) => (
              <li key={p.id}>
                {p.type_libelle} — {p.statut.libelle}
                {p.statut.date_echeance ? ` (${dateFR(p.statut.date_echeance)})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button style={ongletStyle("packs")} onClick={() => setOnglet("packs")}
                data-testid="habilitation-onglet-packs">
          Packs de demande
        </button>
        <button style={ongletStyle("bibliotheque")} onClick={() => setOnglet("bibliotheque")}
                data-testid="habilitation-onglet-bibliotheque">
          Bibliothèque ({pieces.length})
        </button>
        {/* 🔴 L'ANNUAIRE A ENFIN SON ÉCRAN. Il n'était atteignable qu'À
            L'INTÉRIEUR du parcours d'envoi : on pouvait y ajouter une fiche,
            ⛔ jamais la relire ni la corriger. Un annuaire qu'on ne peut pas
            corriger porte ses adresses fausses jusqu'à ce qu'un pli parte au
            mauvais service.
            ⚠️ RÉANCRÉ POUR LA PROD : en preprod ce bouton se pose avant celui
            du « Suivi des démarches » (#863). #863 est LAISSÉ hors de ce port —
            décision JJ, il ne sert pas la capacité d'envoi. L'onglet se place
            donc après « Bibliothèque », qui existe ici. ⛔ Aucun autre écart. */}
        <button style={ongletStyle("annuaire")} onClick={() => setOnglet("annuaire")}
                data-testid="habilitation-onglet-annuaire">
          Annuaire
        </button>
        {/* 🔴 #863 — LE SUIVI DES DÉMARCHES. Une ligne par démarche : le 78 et
            le 94 peuvent diverger, et aucun verdict de pack ne saurait le dire.
            ⚠️ POSÉ APRÈS « Annuaire », qui ⛔ NE BOUGE PAS. En preprod l'ordre
            est inverse : l'annuaire y avait été réancré après « Bibliothèque »
            parce que #863 n'était pas porté. Déplacer l'annuaire maintenant
            ferait bouger un écran en service pour une question d'ordre —
            ⛔ on ne le fait pas. C'est le seul écart PROD/PREPROD assumé ici. */}
        <button style={ongletStyle("demarches")} onClick={() => setOnglet("demarches")}
                data-testid="habilitation-onglet-demarches">
          Suivi des démarches
        </button>
      </div>

      {onglet === "annuaire" && <AnnuaireEcran onErreur={setErreur} />}
      {onglet === "demarches" && <OngletDemarches />}

      {onglet === "packs" && (
        <>
          {packs.map((pack) => (
            <Carte
              key={pack.code}
              titre={pack.libelle}
              sousTitre={`${pack.destinataire} · départements ${(pack.departements || []).join(" · ")}`}
              action={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {/* PRÉSENCE — combien de pièces sont là. Une périmée est là. */}
                  {/* ⚠️ #673 — le compte des manquantes ne porte QUE sur les
                      obligatoires : `nb_manquantes` incluait les complémentaires
                      et annonçait « 3 manquantes » quand une seule bloquait. */}
                  <Badge ton={pack.complet ? "green" : "red"}
                         titre="Présence des pièces OBLIGATOIRES">
                    {pack.complet
                      ? <><CheckCircle2 size={12} style={{ verticalAlign: "-2px" }} /> Complet</>
                      : `${(pack.obligatoires_attendues ?? 0) - (pack.obligatoires_presentes ?? 0)} obligatoire(s) manquante(s)`}
                  </Badge>
                  {/* VALIDITÉ — peut-il partir en l'état. Un 9/9 peut valoir non. */}
                  <Badge ton={pack.pret_a_envoyer ? "green" : "amber"}
                         titre={pack.motif_non_envoyable || "Toutes les pièces présentes sont valides"}>
                    {pack.pret_a_envoyer ? "Prêt à envoyer" : "Non envoyable"}
                  </Badge>
                  {/* 🔴 #860/#863 — LE STATUT EST PROJETÉ, ⛔ plus lu sur le
                      document. Le test binaire `=== "envoye"` était devenu faux :
                      il aurait affiché « En préparation » sur un pack transmis.
                      ⚠️ Ce badge est un RÉSUMÉ — la vérité est dans l'onglet
                      « Suivi des démarches », par territoire. */}
                  <Badge ton={TONS_STATUT_PACK[pack.statut] || "amber"}
                         titre="Résumé projeté — le détail est par démarche">
                    {LIBELLES_STATUT_PACK[pack.statut] || pack.statut}
                  </Badge>
                  <BoutonDiscret
                    onClick={() => setOuvert(ouvert === pack.code ? null : pack.code)}
                    data-testid={`habilitation-ouvrir-${pack.code}`}
                  >
                    {ouvert === pack.code ? "Replier" : "Ouvrir"}
                  </BoutonDiscret>
                </div>
              }
            >
              <p style={{ fontSize: 13, color: "var(--oh-text-secondary)", margin: 0 }}>
                <strong>{pack.obligatoires_presentes}/{pack.obligatoires_attendues}</strong>
                {" pièce(s) obligatoire(s)"}
                {pack.complementaires_attendues > 0 && (
                  <span style={{ color: "var(--oh-text-muted)" }}>
                    {" · "}{pack.complementaires_presentes}/{pack.complementaires_attendues}
                    {" complémentaire(s), sans effet sur l'envoi"}
                  </span>
                )}
                {pack.version_referentiel && (
                  <span style={{ color: "var(--oh-text-muted)" }}>
                    {" · référentiel "}{pack.version_referentiel}
                  </span>
                )}
                {pack.motif_non_envoyable && ` — Non envoyable : ${pack.motif_non_envoyable}.`}
              </p>
              {ouvert === pack.code && (
                <DetailPack pack={pack} packs={packs} onErreur={setErreur}
                            onEnvoiConsigne={consignerEnvoi} onRafraichir={charger} />
              )}
            </Carte>
          ))}
          {!chargement && packs.length === 0 && (
            <Carte titre="Aucune demande">
              <p style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>
                Aucun pack n&apos;est configuré pour cette organisation.
              </p>
            </Carte>
          )}
        </>
      )}

      {onglet === "bibliotheque" && (
        <>
          <Carte
            titre="Déposer une pièce"
            sousTitre="Déposer un type déjà présent crée sa version suivante — rien n'est écrasé, rien n'est dupliqué."
          >
            <FormulaireDepot referentiel={referentiel} packs={packs}
                             onDepose={charger} onErreur={setErreur} />
          </Carte>
          <Carte titre="Bibliothèque de la structure">
            <Bibliotheque pieces={pieces} onRetirer={retirer} onErreur={setErreur} />
          </Carte>
        </>
      )}
    </div>
  );
}
